/**
 * Écran de connexion (O-3) — remplace l'ancien `TokenGate`.
 *
 * Ce n'est plus une garde cosmétique : le couple identifiant / mot de passe part
 * au serveur, qui ouvre une session en cookie `HttpOnly`. Rien n'est conservé
 * dans ce navigateur, et surtout pas le mot de passe.
 *
 * Le style est repris à l'identique de l'écran précédent (`.gate`, `.hero`,
 * `.card`, `.field`, `.btn-primary`) : l'opérateur doit reconnaître son
 * back-office, pas découvrir une application différente le matin du changement.
 *
 * Passe bento (2026-08-24) : le héros bleu gagne son voile de hachures et le
 * titre se réduit à la marque. Rien d'autre ne bouge — un écran de connexion
 * qui change de forme le jour d'une mise à jour fait douter de l'adresse.
 */

import { useState } from 'react';
import { ApiError, api } from '../lib/api';
import type { SessionInfo } from '../lib/api';

export function LoginGate({ onSuccess }: { onSuccess: (session: SessionInfo) => void }): React.ReactElement {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const canSubmit = username.trim() !== '' && password !== '' && !pending;

  const submit = async (): Promise<void> => {
    setPending(true);
    setError(null);
    try {
      onSuccess(await api.login(username.trim(), password));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause : new ApiError(0, { title: String(cause) }, true));
      // Le mot de passe est effacé à chaque échec, l'identifiant non : dans neuf
      // cas sur dix c'est la frappe du mot de passe qui a fauté, et retaper
      // l'identifiant à chaque essai est une punition inutile.
      setPassword('');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="gate">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) void submit();
        }}
      >
        <div className="hero">
          {/* Le titre redevient la seule marque : « YOG MOVE ». Ce qu'est cet
              écran et à qui il s'adresse tient dans la ligne d'en dessous —
              un `h1` qui porte déjà sa propre explication n'est plus un titre. */}
          <h1>YOG MOVE</h1>
          <p>
            Back-office · Dakar 2026. Tableau de bord d'exploitation des navettes des Jeux Olympiques de
            la Jeunesse. Accès réservé au TROC et au COJOJ.
          </p>
        </div>

        {error ? (
          <div className="state state-error" role="alert">
            <span className="state-title">
              {/* Le serveur répond volontairement la même chose pour un identifiant
                  inconnu et un mot de passe faux : on n'invente pas ici une
                  distinction qu'il refuse de faire. */}
              {error.status === 401
                ? 'Identifiant ou mot de passe incorrect.'
                : error.status === 429
                  ? 'Trop de tentatives : patientez avant de réessayer.'
                  : error.userMessage}
            </span>
            {error.problem.detail && error.status !== 401 ? (
              <span className="state-detail">{error.problem.detail}</span>
            ) : null}
          </div>
        ) : null}

        <div className="card stack">
          <label className="field">
            Identifiant
            <input
              type="text"
              name="username"
              value={username}
              autoFocus
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="prenom.nom"
              disabled={pending}
              onChange={(e) => setUsername(e.target.value)}
            />
          </label>

          <label className="field">
            Mot de passe
            <input
              type="password"
              name="password"
              value={password}
              autoComplete="current-password"
              disabled={pending}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          <p className="small muted" style={{ margin: 0 }}>
            La session dure 12 heures, le temps d'une garde. Aucune donnée personnelle d'athlète ne passe par ce tableau de bord.
          </p>

          <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
            {pending ? 'Connexion…' : 'Se connecter'}
          </button>
        </div>
      </form>
    </div>
  );
}
